import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, ArrowRight, Building2, Building, Users, Briefcase, AlertCircle, FileCheck, Shield, Plane, Star, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Pricing = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              {language === 'ar' ? 'شراكات توظيف مرنة لمنظمات الرعاية الصحية' : 'Flexible Recruitment Partnerships for Healthcare Organisations'}
            </h1>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {language === 'ar' 
                ? 'تقدم لوما ميد جلوبال خدمات التوظيف ودعم القوى العاملة المصممة لاحتياجات منظمات الرعاية الصحية، بما في ذلك العيادات والمستشفيات وشبكات الرعاية الصحية والجهات الحكومية.'
                : 'Luma Med Global provides recruitment and workforce support services tailored to the needs of healthcare organisations, including clinics, hospitals, healthcare networks, and government entities.'}
            </p>
            <p className="text-base text-gray-500 max-w-3xl mx-auto mt-4">
              {language === 'ar'
                ? 'تم تصميم هياكل التسعير لتعكس نطاق الخدمة وحجم التوظيف ومستوى التحقق والدعم المطلوب. جميع الأسعار إرشادية وتخضع للاتفاق الرسمي.'
                : 'Pricing structures are designed to reflect service scope, hiring volume, and the level of verification and support required. All pricing is indicative and subject to formal agreement.'}
            </p>
          </div>

          {/* Subscription Plans */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            
            {/* Clinic / Small Practices */}
            <Card className="border-2 border-gray-200 hover:border-[#42b1c2]/50 hover:shadow-xl transition-all">
              <CardHeader className="pb-4">
                <div className="w-12 h-12 bg-[#42b1c2]/10 rounded-xl flex items-center justify-center mb-4">
                  <Building2 className="h-6 w-6 text-[#42b1c2]" />
                </div>
                <div className="text-sm text-gray-500 mb-1">{language === 'ar' ? 'عيادة / ممارسات صغيرة' : 'Clinic / Small Practices'}</div>
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  {language === 'ar' ? 'عيادة' : 'Clinic'}
                </CardTitle>
                <div className="mb-4">
                  <span className="text-sm text-gray-500">{language === 'ar' ? 'من ' : 'From '}</span>
                  <span className="text-3xl font-bold text-[#42b1c2]">SAR 2,999</span>
                  <span className="text-gray-500">{language === 'ar' ? ' /شهرياً' : ' /month'}</span>
                </div>
                <p className="text-sm text-gray-600">
                  {language === 'ar' 
                    ? 'مناسبة للعيادات والممارسات الصحية الصغيرة ذات احتياجات التوظيف المحدودة أو الدورية.'
                    : 'Suitable for clinics and small healthcare practices with limited or periodic hiring needs.'}
                </p>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-500 font-medium mb-3">
                  {language === 'ar' ? 'يشمل الوصول إلى ميزات المنصة مثل:' : 'Includes access to platform features such as:'}
                </p>
                <ul className="space-y-2.5 mb-6">
                  <li className="flex items-start space-x-2 rtl:space-x-reverse">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{language === 'ar' ? 'وظائف نشر الوظائف (وفقاً لحدود الخطة)' : 'Job posting functionality (subject to plan limits)'}</span>
                  </li>
                  <li className="flex items-start space-x-2 rtl:space-x-reverse">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{language === 'ar' ? 'مطابقة المرشحين بمساعدة الذكاء الاصطناعي' : 'AI-assisted candidate matching'}</span>
                  </li>
                  <li className="flex items-start space-x-2 rtl:space-x-reverse">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{language === 'ar' ? 'التحقق من أوراق الاعتماد والمستندات' : 'Credential and document verification'}</span>
                  </li>
                  <li className="flex items-start space-x-2 rtl:space-x-reverse">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{language === 'ar' ? 'أدوات التقارير الأساسية' : 'Basic reporting tools'}</span>
                  </li>
                  <li className="flex items-start space-x-2 rtl:space-x-reverse">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{language === 'ar' ? 'دعم البريد الإلكتروني القياسي' : 'Standard email support'}</span>
                  </li>
                </ul>
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-500 leading-relaxed">
                    <span className="font-medium">{language === 'ar' ? 'ملاحظة:' : 'Note:'}</span> {language === 'ar' 
                      ? 'توفر الخدمة والوصول إلى الميزات والتسعير تخضع للشروط التعاقدية وقد تختلف بناءً على متطلبات التوظيف.'
                      : 'Service availability, feature access, and pricing are subject to contractual terms and may vary based on hiring requirements.'}
                  </p>
                </div>
                <Link to="/register/employer">
                  <Button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900" data-testid="clinic-cta">
                    {language === 'ar' ? 'ابدأ الآن' : 'Get Started'}
                    <ArrowRight className="ms-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Small Hospital */}
            <Card className="border-2 border-[#42b1c2] shadow-xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-[#edd982] text-gray-900 px-4 py-1 rounded-full text-sm font-semibold">
                  {language === 'ar' ? 'الأكثر شيوعاً' : 'Most Popular'}
                </span>
              </div>
              <CardHeader className="pb-4">
                <div className="w-12 h-12 bg-[#42b1c2]/10 rounded-xl flex items-center justify-center mb-4">
                  <Building className="h-6 w-6 text-[#42b1c2]" />
                </div>
                <div className="text-sm text-gray-500 mb-1">{language === 'ar' ? 'مستشفى صغير (50-200 سرير)' : 'Small Hospital (50–200 beds)'}</div>
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  {language === 'ar' ? 'مستشفى صغير' : 'Small Hospital'}
                </CardTitle>
                <div className="mb-4">
                  <span className="text-sm text-gray-500">{language === 'ar' ? 'من ' : 'From '}</span>
                  <span className="text-3xl font-bold text-[#42b1c2]">SAR 9,999</span>
                  <span className="text-gray-500">{language === 'ar' ? ' /شهرياً' : ' /month'}</span>
                </div>
                <p className="text-sm text-gray-600">
                  {language === 'ar' ? 'مصمم للمستشفيات التي تحتاج دعم توظيف مستمر.' : 'Designed for hospitals requiring ongoing recruitment support.'}
                </p>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-500 font-medium mb-3">{language === 'ar' ? 'يشمل الوصول إلى:' : 'Includes access to:'}</p>
                <ul className="space-y-2.5 mb-6">
                  <li className="flex items-start space-x-2 rtl:space-x-reverse">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{language === 'ar' ? 'سعة نشر وظائف موسعة' : 'Expanded job posting capacity'}</span>
                  </li>
                  <li className="flex items-start space-x-2 rtl:space-x-reverse">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{language === 'ar' ? 'أدوات مطابقة AI متقدمة' : 'Advanced AI matching tools'}</span>
                  </li>
                  <li className="flex items-start space-x-2 rtl:space-x-reverse">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{language === 'ar' ? 'سير عمل تحقق محسّن' : 'Enhanced verification workflows'}</span>
                  </li>
                  <li className="flex items-start space-x-2 rtl:space-x-reverse">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{language === 'ar' ? 'دعم المقابلات والتحليلات' : 'Interview support and analytics'}</span>
                  </li>
                  <li className="flex items-start space-x-2 rtl:space-x-reverse">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{language === 'ar' ? 'دعم ذو أولوية وإدارة حساب' : 'Priority support and account management'}</span>
                  </li>
                </ul>
                <div className="bg-[#42b1c2]/5 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    <span className="font-medium">{language === 'ar' ? 'ملاحظة:' : 'Note:'}</span> {language === 'ar' 
                      ? 'يتم تأكيد التسعير النهائي ونطاق الخدمة بعد تقييم حجم التوظيف وتعقيد الدور ومتطلبات التحقق.'
                      : 'Final pricing and service scope are confirmed following an assessment of hiring volume, role complexity, and verification requirements.'}
                  </p>
                </div>
                <Link to="/contact">
                  <Button className="w-full bg-[#42b1c2] hover:bg-[#3a9fb0] text-white" data-testid="hospital-cta">
                    {language === 'ar' ? 'تحدث معنا' : 'Talk to Us'}
                    <ArrowRight className="ms-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Enterprise & Government */}
            <Card className="border-2 border-gray-200 hover:border-[#42b1c2]/50 hover:shadow-xl transition-all bg-gradient-to-br from-gray-50 to-white">
              <CardHeader className="pb-4">
                <div className="w-12 h-12 bg-[#42b1c2]/10 rounded-xl flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-[#42b1c2]" />
                </div>
                <div className="text-sm text-gray-500 mb-1">Large Hospitals & Government</div>
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  Enterprise & Government
                </CardTitle>
                <div className="mb-4">
                  <span className="text-2xl font-bold text-gray-700">Custom Pricing</span>
                </div>
                <p className="text-sm text-gray-600">
                  For large hospitals, healthcare systems, and government entities requiring bespoke 
                  recruitment solutions, integrations, or enterprise-level support.
                </p>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-500 font-medium mb-3">Tailored solutions including:</p>
                <ul className="space-y-2.5 mb-6">
                  <li className="flex items-start space-x-2">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">Custom integrations with HR systems</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">White-label platform options</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">Dedicated account management</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">Volume-based pricing structures</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">Flexible contract terms</span>
                  </li>
                </ul>
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-500 leading-relaxed">
                    <span className="font-medium">Note:</span> Services and pricing are provided on a 
                    case-by-case basis and are subject to formal agreement.
                  </p>
                </div>
                <Link to="/contact">
                  <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white" data-testid="enterprise-cta">
                    Contact Sales
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Pay Per Hire Section */}
          <div className="mb-16">
            <Card className="border-2 border-[#edd982]/50 bg-gradient-to-br from-[#edd982]/10 to-[#42b1c2]/5">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8 items-start">
                  <div>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-[#edd982] rounded-xl flex items-center justify-center">
                        <Briefcase className="h-6 w-6 text-gray-900" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Pay Per Hire</h2>
                        <p className="text-sm text-gray-600">Success-Based Recruitment</p>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <span className="text-lg text-gray-700 font-medium">Variable</span>
                      <span className="text-gray-500"> — calculated as a percentage of the candidate&apos;s first-year base salary</span>
                    </div>

                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start space-x-2">
                        <Check className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">No monthly subscription commitment</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Check className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Fees are payable only upon successful placement</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Check className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Includes recruitment support and candidate verification</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Check className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Access to platform features during the recruitment process</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                      <h3 className="font-semibold text-gray-900 mb-3">Indicative Fee Range</h3>
                      <p className="text-gray-600 mb-4">
                        Typically between <span className="font-semibold text-[#42b1c2]">15% and 25%</span>, 
                        depending on role type, seniority, and recruitment complexity.
                      </p>
                      
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                        <p className="text-sm text-amber-800">
                          <span className="font-medium">Note:</span> All placement fees, replacement terms, 
                          and payment conditions are defined in the applicable service agreement.
                        </p>
                      </div>
                      
                      <Link to="/register/employer">
                        <Button className="w-full bg-[#42b1c2] hover:bg-[#3a9fb0] text-white" data-testid="pay-per-hire-cta">
                          Get Started
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Global Disclaimer */}
          <div className="mb-16">
            <Card className="border border-gray-300 bg-gray-50">
              <CardContent className="p-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Important Information</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      All pricing, features, and services described on this page are indicative only and do not 
                      constitute a binding offer. Final pricing, service scope, timelines, and terms are subject 
                      to formal agreement between Luma Med Global and the client.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ============================================ */}
          {/* OPTIONAL PRE-ARRIVAL SUPPORT PACKAGES */}
          {/* ============================================ */}
          
          <div className="border-t-2 border-gray-200 pt-16 mb-16">
            {/* Section Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-[#42b1c2]/10 rounded-full mb-4">
                <Plane className="h-4 w-4 text-[#42b1c2] mr-2" />
                <span className="text-sm font-medium text-[#42b1c2]">Pre-Arrival Only</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Optional Pre-Arrival Support Packages
              </h2>
              <p className="text-base text-gray-500 mb-2">
                Available at an additional fee | For Medical Professionals & Medical Employers
              </p>
            </div>

            {/* Intro Text */}
            <div className="max-w-4xl mx-auto mb-12">
              <div className="bg-gradient-to-br from-gray-50 to-[#42b1c2]/5 rounded-2xl p-8">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Before travelling to Saudi Arabia, international medical recruitment involves multiple 
                  regulatory, licensing, and compliance steps.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Luma Med Global offers optional pre-arrival support packages to assist medical professionals 
                  and medical employers in preparing for these requirements responsibly and efficiently — 
                  <span className="font-medium"> prior to relocation</span>.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  These services are designed to reduce delays, minimise compliance risks, and provide 
                  structured guidance through complex pre-arrival processes.
                </p>
              </div>
            </div>

            {/* Support Packages Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              
              {/* Essential Package */}
              <Card className="border-2 border-gray-200 hover:border-[#42b1c2]/30 hover:shadow-lg transition-all">
                <CardHeader className="pb-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                    <FileCheck className="h-5 w-5 text-gray-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 mb-1">
                    Essential Pre-Arrival Support
                  </CardTitle>
                  <p className="text-sm text-gray-500">
                    For medical professionals or employers seeking basic pre-arrival guidance
                  </p>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-gray-500 font-medium mb-3">Includes:</p>
                  <ul className="space-y-2.5 mb-6">
                    <li className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">Professional eligibility & qualification pre-check</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">Police clearance & background documentation guidance</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">Saudi work visa preparation guidance</span>
                    </li>
                  </ul>
                  
                  <div className="border-t border-gray-100 pt-4 mb-4">
                    <p className="text-xs text-gray-500 mb-1">Starting from</p>
                    <p className="text-xl font-bold text-gray-900">SAR 1,500 – SAR 2,000</p>
                  </div>
                  
                  <Link to="/contact">
                    <Button variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="essential-package-cta">
                      Enquire
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Professional Compliance Package */}
              <Card className="border-2 border-[#42b1c2] shadow-xl relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-[#42b1c2] text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                    <Star className="h-3 w-3 mr-1" />
                    Recommended
                  </span>
                </div>
                <CardHeader className="pb-4">
                  <div className="w-10 h-10 bg-[#42b1c2]/10 rounded-lg flex items-center justify-center mb-3">
                    <Shield className="h-5 w-5 text-[#42b1c2]" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 mb-1">
                    Professional Compliance Support
                  </CardTitle>
                  <p className="text-sm text-gray-500">
                    For candidates or employers requiring structured regulatory and licensing support
                  </p>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-gray-500 font-medium mb-3">Includes everything in Essential, plus:</p>
                  <ul className="space-y-2.5 mb-6">
                    <li className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">DataFlow (Primary Source Verification) support</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">Saudi Commission for Health Specialties (SCFHS) registration & classification guidance</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">Saudi Prometric exam pathway guidance (if required)</span>
                    </li>
                  </ul>
                  
                  <div className="border-t border-[#42b1c2]/20 pt-4 mb-4">
                    <p className="text-xs text-gray-500 mb-1">Starting from</p>
                    <p className="text-xl font-bold text-[#42b1c2]">SAR 3,000 – SAR 5,000</p>
                  </div>
                  
                  <Link to="/contact">
                    <Button className="w-full bg-[#42b1c2] hover:bg-[#3a9fb0] text-white" data-testid="professional-package-cta">
                      Get Started
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Premium Package */}
              <Card className="border-2 border-gray-200 hover:border-[#42b1c2]/30 hover:shadow-lg transition-all bg-gradient-to-br from-gray-50 to-white">
                <CardHeader className="pb-4">
                  <div className="w-10 h-10 bg-[#edd982]/30 rounded-lg flex items-center justify-center mb-3">
                    <Plane className="h-5 w-5 text-amber-700" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 mb-1">
                    Premium Pre-Arrival End-to-End Support
                  </CardTitle>
                  <p className="text-sm text-gray-500">
                    For medical professionals or employers seeking comprehensive, hands-on pre-arrival coordination
                  </p>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-gray-500 font-medium mb-3">Includes everything in Professional Compliance, plus:</p>
                  <ul className="space-y-2.5 mb-6">
                    <li className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">Medical fitness & health clearance coordination</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">Employment offer & role alignment review (non-legal)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Check className="h-4 w-4 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">Full Saudi work visa documentation coordination and timeline planning</span>
                    </li>
                  </ul>
                  
                  <div className="border-t border-gray-100 pt-4 mb-4">
                    <p className="text-xs text-gray-500 mb-1">Starting from</p>
                    <p className="text-xl font-bold text-gray-900">SAR 5,500 – SAR 9,000</p>
                  </div>
                  
                  <Link to="/contact">
                    <Button variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="premium-package-cta">
                      Enquire
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>

            {/* Recommended Support Level */}
            <div className="max-w-3xl mx-auto mb-12">
              <Card className="border border-[#42b1c2]/30 bg-[#42b1c2]/5">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <Star className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Recommended Support Level</h3>
                      <p className="text-sm text-gray-700 leading-relaxed mb-3">
                        Based on our experience supporting international medical recruitment, the 
                        <span className="font-semibold"> Professional Compliance Support Package</span> is the 
                        most commonly selected option.
                      </p>
                      <p className="text-sm text-gray-600 leading-relaxed mb-3">
                        This level of support provides structured guidance through key regulatory and licensing 
                        steps that most often cause delays or complications when not managed correctly prior to arrival.
                      </p>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Candidates or employers with complex cases, senior roles, or tighter timelines may benefit 
                        from the <span className="font-medium">Premium Pre-Arrival End-to-End Support Package</span>.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Optional Standalone Add-Ons */}
            <div className="mb-12">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 text-center">Optional Standalone Add-Ons</h3>
              <div className="max-w-4xl mx-auto">
                <Card className="border border-gray-200 bg-gray-50/50">
                  <CardContent className="p-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                        <div className="flex items-center space-x-2">
                          <Plus className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">DataFlow Support</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">SAR 1,200 – SAR 1,800</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                        <div className="flex items-center space-x-2">
                          <Plus className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">SCFHS Licensing Guidance</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">SAR 800 – SAR 1,500</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                        <div className="flex items-center space-x-2">
                          <Plus className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">Saudi Prometric Exam Pathway Guidance</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">SAR 600 – SAR 1,100</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                        <div className="flex items-center space-x-2">
                          <Plus className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">Saudi Work Visa Documentation Coordination</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">SAR 1,000 – SAR 1,700</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Who These Packages Are For */}
            <div className="mb-12">
              <div className="max-w-3xl mx-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Who These Packages Are For</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Card className="border border-gray-200">
                    <CardContent className="p-5">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-[#42b1c2]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Users className="h-4 w-4 text-[#42b1c2]" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1">Medical Professionals</h4>
                          <p className="text-sm text-gray-600">
                            Doctors, nurses, and allied health professionals preparing to relocate to Saudi Arabia
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border border-gray-200">
                    <CardContent className="p-5">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-[#42b1c2]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Building className="h-4 w-4 text-[#42b1c2]" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1">Medical Employers</h4>
                          <p className="text-sm text-gray-600">
                            Hospitals, clinics, healthcare groups, and ministries recruiting internationally and seeking 
                            compliance-focused pre-arrival support for their hires
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* Pre-Arrival Disclaimer */}
            <div className="max-w-4xl mx-auto">
              <Card className="border border-amber-200 bg-amber-50/50">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-amber-800 mb-2">Important Disclaimer</h3>
                      <p className="text-sm text-amber-700 leading-relaxed">
                        These services are optional and available at an additional fee. They are intended to support 
                        preparation for regulatory, licensing, and immigration processes prior to travel. Final decisions, 
                        approvals, examinations, and licensing outcomes remain the responsibility of the relevant 
                        regulatory and government authorities.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* FAQ Section */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Common Questions
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    How is final pricing determined?
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Final pricing is determined following a consultation to understand your hiring needs, 
                    role complexity, volume requirements, and level of support needed. All terms are 
                    formalised in a service agreement.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Can pricing be adjusted for volume?
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Yes. Organisations with higher hiring volumes or longer-term recruitment needs may 
                    be eligible for customised pricing structures. Contact our team to discuss options.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    What payment terms are available?
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Payment terms vary by plan type and are outlined in your service agreement. 
                    We offer flexible billing arrangements for enterprise clients.
                  </p>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    What happens after a successful placement?
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Post-placement support and any applicable replacement terms are defined in your 
                    service agreement. Terms vary based on the plan type and specific arrangement.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Are there any setup fees?
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Standard plans typically do not include setup fees. Enterprise solutions may 
                    include implementation fees depending on integration complexity. All fees are 
                    disclosed prior to agreement.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    How do I get started?
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Contact our team to discuss your requirements. We&apos;ll arrange a consultation 
                    to understand your needs and provide a tailored proposal.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Pricing;
